/**
 * Hook that subscribes to MQTT map-bundle status updates for all bundles
 * in an event.
 *
 * Subscribes to `{eventId}/map-bundles/+/status` (wildcard for all bundle IDs)
 * when the component mounts. Parses incoming JSON payloads, validates the
 * status fields, and dispatches `applyStatusUpdate` to update the correct
 * bundle in Redux.
 *
 * Automatically unsubscribes on unmount.
 */

import { useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { applyStatusUpdate, removeBundle } from '../../store/mapBundleSlice';
import { MapBundleStatusUpdate, MapBundleStatusType } from '../../types/mapBundle';
import { mqttService } from '../../services/mqtt';

/** Statuses the Redux slice knows how to handle (update in place). */
const VALID_STATUSES: ReadonlySet<MapBundleStatusType> = new Set([
    'queued',
    'generating',
    'ready',
    'error',
]);

/** Status sent by the backend when a bundle is soft-deleted. */
const DELETED_STATUS = 'deleted';

interface MqttBundlePayload {
    bundle_id: string;
    status: string;
    size_bytes: number | null;
    error: string | null;
    generated_at: string | null;
}

function isValidStatusPayload(value: unknown): value is MqttBundlePayload {
    if (typeof value !== 'object' || value === null) return false;
    const obj = value as Record<string, unknown>;
    if (typeof obj.bundle_id !== 'string') return false;
    if (typeof obj.status !== 'string') return false;
    return VALID_STATUSES.has(obj.status as MapBundleStatusType) || obj.status === DELETED_STATUS;
}

function buildTopic(eventId: string): string {
    return `${eventId}/map-bundles/+/status`;
}

export function useMapBundleMqtt(eventId: string | undefined): void {
    const dispatch = useDispatch<AppDispatch>();

    // Keep a stable ref to the callback so we can register/unregister
    // the exact same function reference with MqttService.
    const callbackRef = useRef<((topic: string, payload: Buffer) => void) | null>(null);

    const handleMessage = useCallback(
        (_topic: string, payload: Buffer) => {
            let parsed: unknown;
            try {
                parsed = JSON.parse(payload.toString());
            } catch {
                console.warn('[MapBundleMqtt] Failed to parse JSON payload, ignoring');
                return;
            }

            if (!isValidStatusPayload(parsed)) {
                console.warn('[MapBundleMqtt] Invalid payload shape or unknown status, ignoring', parsed);
                return;
            }

            if (parsed.status === DELETED_STATUS) {
                dispatch(removeBundle(parsed.bundle_id));
            } else {
                dispatch(applyStatusUpdate(parsed as MapBundleStatusUpdate));
            }
        },
        [dispatch],
    );

    // Store latest handleMessage in the ref so the effect can use it
    // without re-running when dispatch identity changes.
    callbackRef.current = handleMessage;

    useEffect(() => {
        if (!eventId) return;

        const topic = buildTopic(eventId);

        // Wrapper delegates to the ref so the callback identity is
        // stable for the lifetime of this effect.
        const cb = (t: string, p: Buffer) => callbackRef.current?.(t, p);

        mqttService.subscribeWithCallback(topic, cb);

        return () => {
            mqttService.unsubscribeWithCallback(topic, cb);
        };
    }, [eventId]);
}
